import { MdArrowUpward, MdArrowDownward, MdSettings, MdDelete, MdViewColumn } from "react-icons/md";
import { LayoutRow as LayoutRowType } from "./types";
import { buildGridTemplateColumns } from "./layoutUtils";
import { LayoutColumnComponent } from "./LayoutColumn";

interface LayoutRowProps {
  row: LayoutRowType;
  rowIndex: number;
  totalRows: number;
  onMoveRow: (direction: "up" | "down") => void;
  onDeleteRow: () => void;
  onOpenRowSettings: () => void;
  onAddColumn: () => void;
  onDeleteColumn: (columnId: string) => void;
  onOpenColumnSettings: (columnId: string) => void;
  onAddSection: (columnId: string) => void;
  onEditSection: (columnId: string, sectionIndex: number) => void;
  onDeleteSection: (columnId: string, sectionIndex: number) => void;
}

export const LayoutRowComponent = ({
  row,
  rowIndex,
  totalRows,
  onMoveRow,
  onDeleteRow,
  onOpenRowSettings,
  onAddColumn,
  onDeleteColumn,
  onOpenColumnSettings,
  onAddSection,
  onEditSection,
  onDeleteSection,
}: LayoutRowProps) => {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
      style={{
        marginTop: row.marginTop ? `${row.marginTop}px` : undefined,
        marginBottom: row.marginBottom ? `${row.marginBottom}px` : undefined,
      }}
    >
      {/* Row header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 dark:border-slate-700">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Wiersz #{rowIndex + 1}
          <span className="ml-2 text-xs text-slate-400">
            ({row.columns.length} {row.columns.length === 1 ? "kolumna" : "kolumny"})
          </span>
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMoveRow("up")}
            disabled={rowIndex === 0}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-700"
            title="Przesuń w górę"
          >
            <MdArrowUpward size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMoveRow("down")}
            disabled={rowIndex === totalRows - 1}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-700"
            title="Przesuń w dół"
          >
            <MdArrowDownward size={16} />
          </button>
          <button
            type="button"
            onClick={onOpenRowSettings}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            title="Ustawienia wiersza"
          >
            <MdSettings size={16} />
          </button>
          <button
            type="button"
            onClick={onAddColumn}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            title="Dodaj kolumnę"
          >
            <MdViewColumn size={16} />
          </button>
          <button
            type="button"
            onClick={onDeleteRow}
            className="rounded p-1 text-slate-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
            title="Usuń wiersz"
          >
            <MdDelete size={16} />
          </button>
        </div>
      </div>

      {/* Columns grid */}
      <div
        className="p-3"
        style={{
          display: "grid",
          gridTemplateColumns: buildGridTemplateColumns(row.columns),
          gap: `${row.gap ?? 24}px`,
        }}
      >
        {row.columns.map((column) => (
          <LayoutColumnComponent
            key={column.id}
            column={column}
            rowId={row.id}
            onAddSection={() => onAddSection(column.id)}
            onEditSection={(sectionIndex) => onEditSection(column.id, sectionIndex)}
            onDeleteSection={(sectionIndex) => onDeleteSection(column.id, sectionIndex)}
            onOpenSettings={() => onOpenColumnSettings(column.id)}
            onDeleteColumn={() => onDeleteColumn(column.id)}
            canDelete={row.columns.length > 1}
          />
        ))}
      </div>
    </div>
  );
};
